import React, { createContext, useContext, useEffect, useState } from 'react';
import { ref, set, onDisconnect, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { AuthContext } from './AuthContext';

interface GlobalPresenceContextType {
  isOnline: boolean;
  setIsOnline: (online: boolean) => void;
}

const GlobalPresenceContext = createContext<GlobalPresenceContextType | undefined>(undefined);

export const useGlobalPresence = () => {
  const context = useContext(GlobalPresenceContext);
  if (context === undefined) {
    throw new Error('useGlobalPresence must be used within a GlobalPresenceProvider');
  }
  return context;
};

interface GlobalPresenceProviderProps {
  children: React.ReactNode;
}

export const GlobalPresenceProvider: React.FC<GlobalPresenceProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(true);
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  useEffect(() => {
    if (!currentUser) return;

    console.log('Setting up global presence for user:', currentUser.uid);

    // Global presence reference
    const globalPresenceRef = ref(database, `global-presence/${currentUser.uid}`);
    
    // Function to update global presence
    const updateGlobalPresence = () => {
      const presenceData = {
        uid: currentUser.uid,
        displayName: currentUser.displayName || currentUser.email || 'User',
        photoURL: currentUser.photoURL || '',
        email: currentUser.email,
        isOnline: true,
        lastActive: Date.now(),
        platform: navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                 navigator.userAgent.includes('Safari') ? 'Safari' : 'Other'
      };
      
      console.log('Updating global presence:', presenceData);
      set(globalPresenceRef, presenceData);
    };

    // Initial presence update
    updateGlobalPresence();
    setIsOnline(true);

    // Set up heartbeat every 30 seconds to maintain presence
    const heartbeatInterval = setInterval(updateGlobalPresence, 30000);

    // Remove presence on disconnect
    onDisconnect(globalPresenceRef).remove();

    // Clean up presence on page unload
    const handleBeforeUnload = () => {
      set(globalPresenceRef, null);
      setIsOnline(false);
    };

    // Listen for online/offline events
    const handleOnline = () => {
      console.log('User came online');
      setIsOnline(true);
      updateGlobalPresence();
    };

    const handleOffline = () => {
      console.log('User went offline');
      setIsOnline(false);
      set(globalPresenceRef, null);
    };

    // Listen for visibility changes (tab switching)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('Tab became hidden');
        // Don't remove presence, just note the tab is hidden
      } else {
        console.log('Tab became visible');
        updateGlobalPresence();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Clean up presence when component unmounts
      set(globalPresenceRef, null);
      setIsOnline(false);
    };
  }, [currentUser]);

  const value = {
    isOnline,
    setIsOnline
  };

  return (
    <GlobalPresenceContext.Provider value={value}>
      {children}
    </GlobalPresenceContext.Provider>
  );
};

export default GlobalPresenceContext; 